/**
 * Helper class for managing popover positioning and flipping.
 */
export default class PopoverHelper {
  flipClassReplacements: { [key: string]: { [key: string]: string; }; } = {
    'top': {
      'fe-popover-top-left': 'fe-popover-bottom-left',
      'fe-popover-top-center': 'fe-popover-bottom-center',
      'fe-popover-anchor-bottom-center': 'fe-popover-anchor-top-center',
      'fe-popover-top-right': 'fe-popover-bottom-right',
    },
    'left': {
      'fe-popover-top-left': 'fe-popover-top-right',
      'fe-popover-center-left': 'fe-popover-center-right',
      'fe-popover-anchor-center-right': 'fe-popover-anchor-center-left',
      'fe-popover-bottom-left': 'fe-popover-bottom-right',
    },
    'right': {
      'fe-popover-top-right': 'fe-popover-top-left',
      'fe-popover-center-right': 'fe-popover-center-left',
      'fe-popover-anchor-center-left': 'fe-popover-anchor-center-right',
      'fe-popover-bottom-right': 'fe-popover-bottom-left',
    },
    'bottom': {
      'fe-popover-bottom-left': 'fe-popover-top-left',
      'fe-popover-bottom-center': 'fe-popover-top-center',
      'fe-popover-anchor-top-center': 'fe-popover-anchor-bottom-center',
      'fe-popover-bottom-right': 'fe-popover-top-right',
    },
    'top-and-left': {
      'fe-popover-top-left': 'fe-popover-bottom-right',
    },
    'top-and-right': {
      'fe-popover-top-right': 'fe-popover-bottom-left',
    },
    'bottom-and-left': {
      'fe-popover-bottom-left': 'fe-popover-top-right',
    },
    'bottom-and-right': {
      'fe-popover-bottom-right': 'fe-popover-top-left',
    }
  };

  flipMargin: number = 0;

  basePopoverZIndex: number = parseInt(getComputedStyle(document.documentElement)
    .getPropertyValue('--fe-popover-zindex')) || 1450;

  baseTooltipZIndex: number = parseInt(getComputedStyle(document.documentElement)
    .getPropertyValue('--fe-tooltip-zindex')) || 1600;

  /**
   * Calculates the position of the popover based on the provided anchor classes.
   * @param list - List of anchor classes.
   * @param boundingRect - Bounding rectangle of the anchor element.
   * @param selfRect - Bounding rectangle of the popover element.
   * @returns An object containing the top, left, offsetX, and offsetY values.
   */
  calculatePopoverPosition(list: string[], boundingRect: DOMRect, selfRect: DOMRect): { top: number, left: number, offsetX: number, offsetY: number; } {
    let top = 0;
    let left = 0;

    // Determine the base position based on the anchor class
    if (list.indexOf('fe-popover-anchor-top-left') >= 0) {
      left = boundingRect.left;
      top = boundingRect.top;
    } else if (list.indexOf('fe-popover-anchor-top-center') >= 0) {
      left = boundingRect.left + boundingRect.width / 2;
      top = boundingRect.top;
    } else if (list.indexOf('fe-popover-anchor-top-right') >= 0) {
      left = boundingRect.left + boundingRect.width;
      top = boundingRect.top;
    } else if (list.indexOf('fe-popover-anchor-center-left') >= 0) {
      left = boundingRect.left;
      top = boundingRect.top + boundingRect.height / 2;
    } else if (list.indexOf('fe-popover-anchor-center-center') >= 0) {
      left = boundingRect.left + boundingRect.width / 2;
      top = boundingRect.top + boundingRect.height / 2;
    } else if (list.indexOf('fe-popover-anchor-center-right') >= 0) {
      left = boundingRect.left + boundingRect.width;
      top = boundingRect.top + boundingRect.height / 2;
    } else if (list.indexOf('fe-popover-anchor-bottom-left') >= 0) {
      left = boundingRect.left;
      top = boundingRect.top + boundingRect.height;
    } else if (list.indexOf('fe-popover-anchor-bottom-center') >= 0) {
      left = boundingRect.left + boundingRect.width / 2;
      top = boundingRect.top + boundingRect.height;
    } else if (list.indexOf('fe-popover-anchor-bottom-right') >= 0) {
      left = boundingRect.left + boundingRect.width;
      top = boundingRect.top + boundingRect.height;
    }

    let offsetX = 0;
    let offsetY = 0;

    // Determine the offset based on the popover class
    if (list.indexOf('fe-popover-top-left') >= 0) {
      offsetX = 0;
      offsetY = 0;
    } else if (list.indexOf('fe-popover-top-center') >= 0) {
      offsetX = -selfRect.width / 2;
      offsetY = 0;
    } else if (list.indexOf('fe-popover-top-right') >= 0) {
      offsetX = -selfRect.width;
      offsetY = 0;
    } else if (list.indexOf('fe-popover-center-left') >= 0) {
      offsetX = 0;
      offsetY = -selfRect.height / 2;
    } else if (list.indexOf('fe-popover-center-center') >= 0) {
      offsetX = -selfRect.width / 2;
      offsetY = -selfRect.height / 2;
    } else if (list.indexOf('fe-popover-center-right') >= 0) {
      offsetX = -selfRect.width;
      offsetY = -selfRect.height / 2;
    } else if (list.indexOf('fe-popover-bottom-left') >= 0) {
      offsetX = 0;
      offsetY = -selfRect.height;
    } else if (list.indexOf('fe-popover-bottom-center') >= 0) {
      offsetX = -selfRect.width / 2;
      offsetY = -selfRect.height;
    } else if (list.indexOf('fe-popover-bottom-right') >= 0) {
      offsetX = -selfRect.width;
      offsetY = -selfRect.height;
    }

    return {
      top: top,
      left: left,
      offsetX: offsetX,
      offsetY: offsetY
    };
  }

  /**
   * Calculates the position of the popover for a flipped configuration.
   * @param inputArray - List of original classes.
   * @param selector - Flip direction selector.
   * @param boundingRect - Bounding rectangle of the anchor element.
   * @param selfRect - Bounding rectangle of the popover element.
   * @returns An object containing the top, left, offsetX, and offsetY values.
   */
  getPositionForFlippedPopver(inputArray: string[], selector: string, boundingRect: DOMRect, selfRect: DOMRect): { top: number, left: number, offsetX: number, offsetY: number; } {
    const classList: string[] = [];
    for (let i = 0; i < inputArray.length; i++) {
      const item = inputArray[i];
      const replacements = this.flipClassReplacements[selector][item];
      if (replacements) {
        classList.push(replacements);
      } else {
        classList.push(item);
      }
    }

    return this.calculatePopoverPosition(classList, boundingRect, selfRect);
  }

  /**
   * Places the popover element based on the provided class selector.
   * @param popoverNode - The popover DOM element.
   * @param classSelector - The class selector to use for positioning.
   */
  placePopover(popoverNode: HTMLElement, classSelector?: string): void {
    if (popoverNode && popoverNode.parentNode) {
      const id = popoverNode.id.substr(8);
      const popoverContentNode = document.getElementById('popovercontent-' + id);

      if (!popoverContentNode) {
        return;
      }
      const classList = popoverContentNode.classList;

      if (!classList.contains('fe-popover-open')) {
        return;
      }

      if (classSelector && !classList.contains(classSelector)) {
        return;
      }

      let boundingRect: DOMRect = (popoverNode.parentNode as HTMLElement).getBoundingClientRect();

      // allow them to be changed after initial creation
      popoverContentNode.style['max-width'] = 'none';
      popoverContentNode.style['min-width'] = 'none';

      if (classList.contains('fe-popover-relative-width')) {
        popoverContentNode.style['max-width'] = (boundingRect.width) + 'px';
      }

      const selfRect = popoverContentNode.getBoundingClientRect();
      const classListArray = Array.from(classList);

      const position = this.calculatePopoverPosition(classListArray, boundingRect, selfRect);
      const graceMargin = this.flipMargin;
      let left = position.left;
      let top = position.top;
      let offsetX = position.offsetX;
      let offsetY = position.offsetY;

      if (classList.contains('fe-popover-position-override')) {
        left = parseInt(popoverContentNode.style['left']) || left;
        top = parseInt(popoverContentNode.style['top']) || top;
        offsetX = 0;
        offsetY = 0;
        boundingRect = new DOMRect(left, top, selfRect.width, selfRect.height);
      }

      if (classList.contains('fe-popover-overflow-slide-onopen')) {
        // Check if the popover is cut off on the right edge of the screen
        const rightEdge = left + offsetX + selfRect.width;
        if (rightEdge > boundingRect.width) {
          offsetX -= (rightEdge - boundingRect.width) + graceMargin;
        }

        // Check if the popover is cut off on the left edge of the screen
        const leftEdge = left + offsetX;
        if (leftEdge < 0) {
          offsetX -= leftEdge - graceMargin;
        }
      }

      if (classList.contains('fe-popover-overflow-flip-onopen') || classList.contains('fe-popover-overflow-flip-always')) {
        const appBarElements = document.getElementsByClassName("fe-appbar fe-appbar-fixed-top");
        let appBarOffset = 0;
        if (appBarElements.length > 0) {
          appBarOffset = (appBarElements[0] as HTMLElement).getBoundingClientRect().height;
        }

        const deltaToLeft = left + offsetX;
        const deltaToRight = window.innerWidth - left - selfRect.width;
        const deltaTop = top - selfRect.height - appBarOffset;
        const spaceToTop = top - appBarOffset;
        const deltaBottom = window.innerHeight - top - selfRect.height;

        let selector = (popoverContentNode as any).popoverFlipped;

        if (!selector) {
          if (classList.contains('fe-popover-top-left')) {
            if (deltaBottom < graceMargin && deltaToRight < graceMargin && spaceToTop >= selfRect.height && deltaToLeft >= selfRect.width) {
              selector = 'top-and-left';
            } else if (deltaBottom < graceMargin && spaceToTop >= selfRect.height) {
              selector = 'top';
            } else if (deltaToRight < graceMargin && deltaToLeft >= selfRect.width) {
              selector = 'left';
            }
          } else if (classList.contains('fe-popover-top-center')) {
            if (deltaBottom < graceMargin && spaceToTop >= selfRect.height) {
              selector = 'top';
            }
          } else if (classList.contains('fe-popover-top-right')) {
            if (deltaBottom < graceMargin && deltaToLeft < graceMargin && spaceToTop >= selfRect.height && deltaToRight >= selfRect.width) {
              selector = 'top-and-right';
            } else if (deltaBottom < graceMargin && spaceToTop >= selfRect.height) {
              selector = 'top';
            } else if (deltaToLeft < graceMargin && deltaToRight >= selfRect.width) {
              selector = 'right';
            }
          } else if (classList.contains('fe-popover-center-left')) {
            if (deltaToRight < graceMargin && deltaToLeft >= selfRect.width) {
              selector = 'left';
            }
          } else if (classList.contains('fe-popover-center-right')) {
            if (deltaToLeft < graceMargin && deltaToRight >= selfRect.width) {
              selector = 'right';
            }
          } else if (classList.contains('fe-popover-bottom-left')) {
            if (deltaTop < graceMargin && deltaToRight < graceMargin && deltaBottom >= 0 && deltaToLeft >= selfRect.width) {
              selector = 'bottom-and-left';
            } else if (deltaTop < graceMargin && deltaBottom >= 0) {
              selector = 'bottom';
            } else if (deltaToRight < graceMargin && deltaToLeft >= selfRect.width) {
              selector = 'left';
            }
          } else if (classList.contains('fe-popover-bottom-center')) {
            if (deltaTop < graceMargin && deltaBottom >= 0) {
              selector = 'bottom';
            }
          } else if (classList.contains('fe-popover-bottom-right')) {
            if (deltaTop < graceMargin && deltaToLeft < graceMargin && deltaBottom >= 0 && deltaToRight >= selfRect.width) {
              selector = 'bottom-and-right';
            } else if (deltaTop < graceMargin && deltaBottom >= 0) {
              selector = 'bottom';
            } else if (deltaToLeft < graceMargin && deltaToRight >= selfRect.width) {
              selector = 'right';
            }
          }
        }

        if (selector && selector != 'none') {
          const newPosition = this.getPositionForFlippedPopver(classListArray, selector, boundingRect, selfRect);
          left = newPosition.left;
          top = newPosition.top;
          offsetX = newPosition.offsetX;
          offsetY = newPosition.offsetY;
          popoverContentNode.setAttribute('data-popover-flip', 'flipped');
        } else {
          if (left + offsetX < 0 && Math.abs(left + offsetX) < selfRect.width) {
            left = Math.max(0, left + offsetX);
            offsetX = 0;
          }

          if (top + offsetY < appBarOffset && appBarElements.length > 0) {
            this.updatePopoverZIndex(popoverContentNode, appBarElements[0] as HTMLElement);
          }

          if (top + offsetY < 0 && Math.abs(top + offsetY) < selfRect.height) {
            top = Math.max(0, top + offsetY);
            offsetY = 0;
          }

          popoverContentNode.removeAttribute('data-popover-flip');
        }

        if (classList.contains('fe-popover-overflow-flip-onopen')) {
          if (!(popoverContentNode as any).popoverFlipped) {
            (popoverContentNode as any).popoverFlipped = selector || 'none';
          }
        }
      }

      if (window.getComputedStyle(popoverNode).position == 'fixed') {
        popoverContentNode.style['position'] = 'fixed';
      }
      else if (!classList.contains('fe-popover-fixed')) {
        offsetX += window.scrollX;
        offsetY += window.scrollY;
      }

      if (classList.contains('fe-popover-position-override')) {
        offsetX = 0;
        offsetY = 0;
      }

      popoverContentNode.style['left'] = (left + offsetX) + 'px';
      popoverContentNode.style['top'] = (top + offsetY) + 'px';

      this.updatePopoverZIndex(popoverContentNode, popoverNode.parentNode as HTMLElement);

      if (window.getComputedStyle(popoverNode).getPropertyValue('z-index') != 'auto') {
        popoverContentNode.style['z-index'] = window.getComputedStyle(popoverNode).getPropertyValue('z-index');
        (popoverContentNode as any).skipZIndex = true;
      }
    }
  }

  /**
   * Places all popovers by the provided class selector.
   * @param classSelector - The class selector to use for positioning.
   */
  placePopoverByClassSelector(classSelector: string | null = null): void {
    const items = (window as any).FeBlazorLibrary.popover.getAllObservedContainers();

    for (let i = 0; i < items.length; i++) {
      const popoverNode = document.getElementById('popover-' + items[i]);
      if (popoverNode) {
        (window as any).FeBlazorLibrary.popoverHelper.placePopover(popoverNode, classSelector);
      }
    }
  }

  /**
   * Places the popover by the target node.
   * @param target - The target DOM element.
   */
  placePopoverByNode(target: HTMLElement): void {
    const id = target.id.substr(15);
    const popoverNode = document.getElementById('popover-' + id);
    if (popoverNode) {
      this.placePopover(popoverNode);
    }
  }

  /**
   * Counts the number of popover providers.
   * @returns The number of popover providers.
   */
  countProviders(): number {
    return document.querySelectorAll(".fe-popover-provider").length;
  }

  /**
   * Updates the z-index of the popover content node based on its parent node.
   * @param popoverContentNode - The popover content DOM element.
   * @param parentNode - The parent DOM element.
   */
  updatePopoverZIndex(popoverContentNode: HTMLElement, parentNode: HTMLElement): void {
    const parentPopover = parentNode.closest('.fe-popover');
    const parentOfPopover = popoverContentNode.parentNode as HTMLElement;
    let newZIndex = this.basePopoverZIndex + 1;
    const origZIndex = parseInt(popoverContentNode.style['z-index']) || 1;

    if (parentPopover) {
      const computedStyle = window.getComputedStyle(parentPopover);
      const parentZIndexValue = computedStyle.getPropertyValue('z-index');
      if (parentZIndexValue !== 'auto') {
        newZIndex = parseInt(parentZIndexValue) + 1;
      }
      popoverContentNode.style['z-index'] = newZIndex.toString();
    } else if (parentOfPopover) {
      const computedStyle = window.getComputedStyle(parentOfPopover);
      const tooltipZIndexValue = computedStyle.getPropertyValue('z-index');
      if (tooltipZIndexValue !== 'auto') {
        newZIndex = parseInt(tooltipZIndexValue) + 1;
      }
      popoverContentNode.style['z-index'] = newZIndex.toString();
    } else if (parentOfPopover) {
      const computedStyle = window.getComputedStyle(parentOfPopover);
      const tooltipZIndexValue = computedStyle.getPropertyValue('z-index');
      if (tooltipZIndexValue !== 'auto') {
        newZIndex = parseInt(tooltipZIndexValue) + 1;
      }
      popoverContentNode.style['z-index'] = Math.max(newZIndex, this.baseTooltipZIndex + 1, origZIndex).toString();
    } else if (parentNode && parentNode.classList.contains("fe-tooltip-root")) {
      const computedStyle = window.getComputedStyle(parentNode);
      const tooltipZIndexValue = computedStyle.getPropertyValue('z-index');
      if (tooltipZIndexValue !== 'auto') {
        newZIndex = parseInt(tooltipZIndexValue) + 1;
      }
      popoverContentNode.style['z-index'] = Math.max(newZIndex, this.baseTooltipZIndex + 1).toString();
    } else if (parentNode && parentNode.classList.contains("fe-appbar")) {
      const computedStyle = window.getComputedStyle(parentNode);
      const appbarZIndexValue = computedStyle.getPropertyValue('z-index');
      if (appbarZIndexValue !== 'auto') {
        newZIndex = parseInt(appbarZIndexValue) + 1;
      }
      popoverContentNode.style['z-index'] = newZIndex.toString();
    }
  }

  /**
   * Adds a scroll listener to the provided node.
   * @param node - The node to add the scroll listener to.
   */
  popoverScrollListener(node: HTMLElement): void {
    let currentNode: HTMLElement | null = node.parentNode as HTMLElement;
    while (currentNode) {
      const isScrollable =
        currentNode.scrollHeight > currentNode.clientHeight || // Vertical scroll
        currentNode.scrollWidth > currentNode.clientWidth;    // Horizontal scroll
      if (isScrollable) {
        currentNode.addEventListener('scroll', () => {
          window.FeBlazorLibrary.popoverHelper.placePopoverByClassSelector('fe-popover-fixed');
          window.FeBlazorLibrary.popoverHelper.placePopoverByClassSelector('fe-popover-overflow-flip-always');
        });
      }
      // Stop if we reach the body, or head
      if (currentNode.tagName === "BODY") {
        break;
      }
      currentNode = currentNode.parentNode as HTMLElement;
    }
  }
}